// @flow

import gql from 'graphql-tag';
import React, { type Node, createContext, useContext, useState } from 'react';
import { get } from 'lodash';

import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH } from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalSidebar';
import { isBrowser } from '@zapier/common-utils';
// TODO: Would be nice to pull `useMediaProps` into a more shareable location.
// eslint-disable-next-line
import { useMediaProps } from '@zapier/common-components';
import { useQuery } from '@apollo/react-hooks';
import { useBandit, useSwitch } from '@zapier/toolbox-ab-testing';

const defaultContext = {
  accountSwitcherUrl: '',
  currentAccountId: undefined,
  currentUrl: '',
  isLoggedIn: false,
  isLoadingLoggedIn: true,
  isLoadingExperiment: true,
  isOnAppPage: false,
  isSidebarRendered: false,
  isSidebarControlledByTopbar: false,
  isSidebarOpenedByTopbar: false,
  sidebarWidth: 0,
  canTopbarFitLinks: false,
  onClickCollapse: () => {},
  // eslint-disable-next-line no-unused-vars
  setIsSidebarOpenedByTopbar: (isRendered: boolean) => {},
  // eslint-disable-next-line no-unused-vars
  setIsSidebarRendered: (isRendered: boolean) => {},
  shouldRenderSidebar: false,
  shouldRenderTopbarLinks: false,
  shouldRenderUniversalNav: false,
  topbarSize: 'medium',
};

const UniversalNavContext = createContext(defaultContext);

export const useUniversalNavContext = () => useContext(UniversalNavContext);

type Props = {
  /**
   * URL to change to when switching accounts.
   * If empty, defaults to the current URL.
   */
  accountSwitcherUrl?: string,
  /**
   * What to render within the provider.
   */
  children: Node,
  /**
   * `id` of the current account. Will be falsey if user isn't logged in.
   */
  currentAccountId?: number,
  /**
   * Current path in the browser's address bar.
   */
  currentUrl: string,
};

export const isAuthenticatedQuery = gql`
  query isAuthenticatedQuery {
    isAuthenticatedV2
  }
`;

const isCurrentUrlAnAuthPage = (currentUrl: string) => {
  return (
    currentUrl.includes('/login') ||
    currentUrl.includes('/sign-up') ||
    currentUrl.includes('/oauth/authorize')
  );
};

const isCurrentUrlAnAppPage = (currentUrl: string) => {
  return currentUrl.includes('/app/');
};

const shouldRenderSidebarForRoute = ({ currentUrl }) => {
  const sidebarRoutesBlacklist = [
    '/onboarding',
    '/login',
    '/sign-up',
    '/oauth/authorize',
  ];
  return sidebarRoutesBlacklist.every(route => {
    return !currentUrl.includes(route);
  });
};

const shouldRenderTopbarLinksForRoute = ({ currentUrl }) => {
  return (
    !isCurrentUrlAnAppPage(currentUrl) && !isCurrentUrlAnAuthPage(currentUrl)
  );
};

const getShouldRenderSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
}) => {
  if (!isBrowser()) {
    return false;
  }
  if (isSidebarControlledByTopbar) {
    return isSidebarOpenedByTopbar;
  }
  if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl })) {
    return true;
  }
  if (!canTopbarFitLinks && shouldRenderTopbarLinksForRoute({ currentUrl })) {
    return true;
  }
  return false;
};

const getShouldRenderTopbarToggle = ({
  isSidebarControlledByTopbar,
  isLoggedIn,
  currentUrl,
}) => {
  // If the sidebar isn't controlled by the topbar then
  // the toggle should never render, so bail immediately.
  if (!isSidebarControlledByTopbar) {
    return false;
  }
  if (isLoggedIn) {
    return true;
  }
  return shouldRenderTopbarLinksForRoute({
    currentUrl,
  });
};

export const UniversalNavProvider = (props: Props) => {
  const {
    isSidebarControlledByTopbar,
    canTopbarFitLinks,
    topbarSize,
  } = useMediaProps({
    '(min-width: 0px)': {
      isSidebarControlledByTopbar: true,
      canTopbarFitLinks: false,
      topbarSize: 'small',
    },
    '(min-width: 400px)': {
      isSidebarControlledByTopbar: false,
      topbarSize: 'medium',
    },
    '(min-width: 940px)': {
      canTopbarFitLinks: true,
    },
  });
  const { currentUrl } = props;
  const isOnAppPage = isCurrentUrlAnAppPage(currentUrl);
  const shouldRenderTopbarLinks =
    canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({
      currentUrl,
    });
  const { data, loading } = useQuery(isAuthenticatedQuery, {
    // Always recheck whether the user is authenticated.
    // This is especially important when transitioning from
    // being logged out to logged in and vice versa.
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });
  const isLoggedIn = data && data.isAuthenticatedV2;
  const switchResult = useSwitch('universalNavigation');
  const banditResult = useBandit(
    {
      name: 'design-system-universal-navigation',
      options: ['control', 'treatment'],
    },
    { shouldSkip: !switchResult.isSwitchOn }
  );
  const shouldRenderUniversalNav =
    switchResult.isSwitchOn &&
    get(banditResult.result, ['recommendation'], []).includes('treatment');
  const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const shouldRenderSidebar = getShouldRenderSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
  });
  const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
    isSidebarControlledByTopbar,
    isLoggedIn,
    currentUrl,
  });
  const onClickCollapse = shouldRenderTopbarToggle
    ? () => {
        setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
      }
    : undefined;
  const value = {
    accountSwitcherUrl: props.accountSwitcherUrl,
    currentAccountId: props.currentAccountId,
    isLoadingLoggedIn: loading,
    isLoadingExperiment: switchResult.isLoading || banditResult.isLoading,
    currentUrl,
    isLoggedIn,
    isOnAppPage,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    isSidebarRendered,
    canTopbarFitLinks,
    onClickCollapse,
    setIsSidebarOpenedByTopbar,
    setIsSidebarRendered,
    shouldRenderSidebar,
    shouldRenderTopbarLinks,
    shouldRenderUniversalNav,
    sidebarWidth: isSidebarRendered ? UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH : 0,
    topbarSize,
  };
  return (
    <UniversalNavContext.Provider value={value}>
      {props.children}
    </UniversalNavContext.Provider>
  );
};
